import React, { useState } from 'react';
import { Button, Form, Modal, Row, Select, Space, Table } from 'antd';
import { observer } from 'mobx-react-lite';
import { useForm } from 'antd/es/form/Form';
import { toJS } from 'mobx';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import LoanLayout from '../LoanLayout';
import { useLoan } from '../../stores/store';
import Loading from '../Loading';
import ButtonNav from '../../components/ButtonNav/ButtonNav';
import { stepFinished } from '../../nav';
import PlaceholderLabel from '../../components/PlaceholderLabel/PlaceholderLabel';
import MoneyInput from '../../components/MoneyInput/MoneyInput';
import { ReactComponent as SelectArrowIcon } from '../../icons/select-arrow-icon.svg';

import styles from './financial.module.scss';
import { formatCurrency } from '../../utils';
import { createSchemaValidator } from '../../validators/schemaValidator';
import { pickFields } from './meta';
import AppFormItem from '../components/AppFormItem';
import { Asset, AssetName, Borrower, LoanPurpose } from '../../schema';
import { StepFooter } from '../components/StepFooter/StepFooter';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FF_FULL_APPLICATION_STYLING } from '../../constants';

const formItemLayout = {
  labelCol: { span: 0 },
  wrapperCol: { span: 24 },
};

type Props = {};

let editingCol: number | null = null;

const schemaValidator = createSchemaValidator(pickFields('assets'));

const AssetStep: React.FC<Props> = observer(function AssetStep() {
  const loanStore = useLoan();
  const { loan } = loanStore;
  const [form] = useForm();
  const [showAdd, setShowAdd] = useState(false);
  const [modalAsset, setModalAsset] = useState<Partial<Asset>>({});
  const isNewFunnel = useFeatureFlag({
    flag: FF_FULL_APPLICATION_STYLING,
  });

  if (!loan || loanStore.status === 'pending') {
    return <Loading />;
  }
  const borrower = loan.borrower as Borrower;
  const statement = borrower?.financialStatement;
  if (!statement || !statement.assets) {
    loanStore.setupAssets(borrower);
    return null;
  }

  const handleSubmitAsset = () => {
    loanStore.addAsset(borrower, modalAsset as Asset, editingCol);
    // noinspection JSIgnoredPromiseFromCall
    loanStore.syncDoc();
    setShowAdd(false);
    form.resetFields();
    editingCol = null;
  };

  const handleCancel = () => {
    setShowAdd(false);
    form.resetFields();
    editingCol = null;
    return false;
  };

  const columns: ColumnsType<Asset> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Amount',
      dataIndex: 'value',
      key: 'value',
      render: (text: any) => formatCurrency(text),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (text: any, record: Asset, index: number) => (
        <Space size="middle">
          <Button
            onClick={() => {
              editingCol = index;
              form.setFieldsValue(record);
              setModalAsset({});
              setShowAdd(true);
            }}
          >
            <EditOutlined />
          </Button>
          <Button
            onClick={() => {
              loanStore.removeAsset(borrower, index);
            }}
          >
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <LoanLayout className="assetsLayout">
      {isNewFunnel && <div className="step-title">About You</div>}
      <h1>
        {loan?.purpose === LoanPurpose.boat
          ? 'We need a little more financial information'
          : 'Liquid/Retirement Assets'}
      </h1>
      {loan?.purpose === LoanPurpose.boat && <p>Add your assets below (optional).</p>}
      {loan?.purpose !== LoanPurpose.boat && (
        <p className="assetsSubtitle">
          For this loan amount we'll need a financial statement. Please list your assets below.
        </p>
      )}
      <Table dataSource={toJS(statement.assets)} columns={columns} pagination={false} />

      <Modal title="Add Asset" visible={showAdd} footer={false} onCancel={handleCancel}>
        <Form form={form} {...formItemLayout} onFinish={handleSubmitAsset}>
          <AppFormItem name="name" required rules={[schemaValidator('name', modalAsset)]}>
            <PlaceholderLabel label="Type of asset">
              <Select
                suffixIcon={<SelectArrowIcon />}
                showSearch={Object.keys(AssetName).length > 7}
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {Object.entries(AssetName).map(([key, label]) => (
                  <Select.Option key={key} value={label}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="value"
            required
            rules={[
              schemaValidator('value', modalAsset, {
                message: 'Please enter the amount',
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Amount">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <Row justify="end">
            <Button name="cancel" type="default" htmlType="button" onClick={handleCancel}>
              Cancel
            </Button>

            <Button name="ok" className={styles.addButton} type="primary" htmlType="submit">
              Okay
            </Button>
          </Row>
        </Form>
      </Modal>

      <Row className={styles.tableActionRow}>
        <Button
          name="add"
          type="default"
          htmlType="button"
          onClick={() => {
            setShowAdd(true);
            return false;
          }}
        >
          <PlusOutlined />
          Add Asset
        </Button>
      </Row>

      <ButtonNav onNext={stepFinished} />
      <StepFooter type="secure" />
    </LoanLayout>
  );
});

export default AssetStep;
